import { forwardRef, useMemo, useState } from 'react'

/**
 * Who a new proposal is for: a searchable list of clients and open leads.
 *
 * Replaces a long <select> with optgroups, where every row read
 * "Company · Name (lead)" in one grey line. Each row now shows the company,
 * the person and their email, with leads tagged, so the right one is found by
 * reading, not by squinting. Typing filters across name, company and email.
 */

const initials = (s = '') => s.replace(/&/g, ' ').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?'

const css = `
.rp-row { all: unset; box-sizing: border-box; display: flex; align-items: center; gap: 12px; width: 100%; padding: 9px 12px; cursor: pointer; font-family: var(--fu); }
.rp-row:hover { background: var(--surf); }
.rp-row:focus-visible { outline: 2px solid var(--accent); outline-offset: -2px; }
.rp-row[aria-selected="true"] { background: var(--accent-soft); }
.rp-chip { all: unset; cursor: pointer; font-family: var(--fu); font-size: 12px; font-weight: 600; padding: 5px 10px; border: 1px solid var(--bdr2); color: var(--txt); }
.rp-chip[aria-pressed="true"] { background: var(--accent-soft); color: var(--accent); border-color: color-mix(in srgb, var(--accent) 45%, transparent); }
.rp-chip:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }
`

const RecipientPicker = forwardRef(function RecipientPicker({ clients = [], leads = [], value = '', onChange, keyFor }, ref) {
  const [q, setQ] = useState('')
  const [only, setOnly] = useState('all')

  const rows = useMemo(() => {
    const all = [
      ...clients.map(c => ({ key: keyFor('client', c.id), type: 'client', title: c.company || c.name || 'Unnamed client', person: c.company ? c.name : '', email: c.email || '' })),
      ...leads.map(l => ({ key: keyFor('lead', l.id), type: 'lead', title: l.company || l.name || 'Unnamed lead', person: l.company ? l.name : '', email: l.email || '', note: [l.status, l.project_type].filter(Boolean).join(' · ') })),
    ]
    const needle = q.trim().toLowerCase()
    return all.filter(r => (only === 'all' || r.type === only)
      && (!needle || [r.title, r.person, r.email].some(v => (v || '').toLowerCase().includes(needle))))
  }, [clients, leads, q, only, keyFor])

  const counts = { all: clients.length + leads.length, client: clients.length, lead: leads.length }

  return (
    <div style={{ border: '1px solid var(--bdr2)', background: 'var(--card)' }}>
      <style>{css}</style>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', padding: 10, borderBottom: '1px solid var(--bdr)' }}>
        <input ref={ref} type="search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search name, company or email"
          aria-label="Search clients and leads"
          style={{ flex: '1 1 200px', minWidth: 0, height: 34, padding: '0 10px', border: '1px solid var(--bdr2)', background: 'var(--card)', color: 'var(--txt)', fontFamily: 'var(--fu)', fontSize: 13 }} />
        <div role="group" aria-label="Show" style={{ display: 'flex', gap: 6 }}>
          {[['all', 'All'], ['client', 'Clients'], ['lead', 'Leads']].map(([id, label]) => (
            <button key={id} type="button" className="rp-chip" aria-pressed={only === id} onClick={() => setOnly(id)}>{label} {counts[id]}</button>
          ))}
        </div>
      </div>
      <div role="listbox" aria-label="Who this proposal is for" style={{ maxHeight: 260, overflowY: 'auto' }}>
        <button type="button" role="option" aria-selected={value === ''} className="rp-row" onClick={() => onChange('')}>
          <span aria-hidden="true" style={{ width: 32, height: 32, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px dashed var(--bdr2)', color: 'var(--mut)', fontSize: 15 }}>–</span>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--txt)' }}>Decide later</span>
          <span style={{ fontSize: 12, color: 'var(--mut)' }}>Pick the client in the studio</span>
        </button>
        {rows.map(r => {
          const on = value === r.key
          return (
            <button key={r.key} type="button" role="option" aria-selected={on} className="rp-row" onClick={() => onChange(r.key)}>
              <span aria-hidden="true" style={{ width: 32, height: 32, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--surf)', border: '1px solid var(--bdr)', fontSize: 11, fontWeight: 700, color: 'var(--txt)', letterSpacing: '0.03em' }}>{initials(r.title)}</span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                  <span style={{ fontSize: 13.5, fontWeight: 700, color: on ? 'var(--accent)' : 'var(--txt)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.title}</span>
                  {r.type === 'lead' && <span style={{ flexShrink: 0, fontSize: 11, fontWeight: 600, color: 'var(--mut)', border: '1px solid var(--bdr2)', padding: '0 6px', background: 'var(--surf)' }}>Lead</span>}
                </span>
                <span style={{ display: 'block', fontSize: 12, color: 'var(--mut)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2 }}>
                  {[r.person, r.email, r.note].filter(Boolean).join('  ·  ') || 'No contact details'}
                </span>
              </span>
              {on && <svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="square"><path d="M3 8.5l3.2 3L13 4.5" /></svg>}
            </button>
          )
        })}
        {rows.length === 0 && <div style={{ padding: '14px 12px', fontFamily: 'var(--fu)', fontSize: 13, color: 'var(--mut)' }}>Nobody matches “{q}”.</div>}
      </div>
    </div>
  )
})

export default RecipientPicker
